import React from 'react';
import type { OrderStatusUpdateRowProps } from './types';

// Format date/time in the style shown in mockup: "Fri 10:47 AM PT"
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
  const time = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${dayName} ${time} PT`;
};

const OrderStatusUpdateRow: React.FC<OrderStatusUpdateRowProps> = ({ update, brand }) => {
  return (
    <tr
      className="border-b last:border-b-0 hover:bg-gray-50"
      style={{ borderColor: brand.colors.border }}
    >
      {/* Date/Time */}
      <td
        className="px-3 py-2 text-xs whitespace-nowrap"
        style={{ color: brand.colors.text }}
      >
        {formatDateTime(update.createdAt)}
      </td>

      {/* Status / Description */}
      <td
        className="px-3 py-2 text-xs"
        style={{ color: brand.colors.text }}
      >
        <div>
          {update.statusLabel}
          {update.emailNote && (
            <span
              className="ml-1 text-xs italic"
              style={{ color: brand.colors.textLight }}
            >
              ({update.emailNote})
            </span>
          )}
        </div>
      </td>

      {/* Related Orders */}
      <td
        className="px-3 py-2 text-xs"
        style={{ color: brand.colors.textLight }}
      >
        {update.relatedOrders && update.relatedOrders.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {update.relatedOrders.map((order) => (
              <span
                key={order.id}
                className="text-xs"
                style={{ color: brand.colors.link }}
              >
                {order.orderNumber}
              </span>
            ))}
          </div>
        ) : null}
      </td>

      {/* Internal */}
      <td
        className="px-3 py-2 text-xs text-center"
        style={{ color: brand.colors.text }}
      >
        {update.isInternal ? 'Yes' : 'No'}
      </td>

      {/* Email Note */}
      <td
        className="px-3 py-2 text-xs"
        style={{ color: brand.colors.textLight }}
      >
        {update.emailNotification?.recipients?.[0] || update.note || ''}
      </td>

      {/* Status By */}
      <td
        className="px-3 py-2 text-xs"
        style={{ color: brand.colors.text }}
      >
        {update.createdByName}
      </td>

      {/* Action */}
      <td className="px-3 py-2 text-xs">
        <div className="flex items-center gap-1">
          <button
            className="p-1 rounded hover:bg-gray-100"
            title="Edit"
            style={{ color: brand.colors.link }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          <button
            className="p-1 rounded hover:bg-red-50"
            title="Delete"
            style={{ color: brand.colors.error }}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};

export default OrderStatusUpdateRow;
